import React from "react";
import styled from "styled-components";
import { colors, typography, spacing } from "../styles/designSystem";
import {
    Panel,
    PanelLabel,
    Caption,
    PipelineFlow,
    CycleFlow,
} from "./diagrams/primitives";

/**
 * Diagrams for the AT&T case study.
 *
 * The argument is that a design system now has a third consumer, and that consumer
 * needs the system to be machine-readable. Each diagram carries one step of it: who
 * consumes the system, how the layers connect, what the agent is forbidden to do,
 * and why generation without validation is only half a pipeline.
 */

// ===== Three consumers of one design system =====
const Consumers = styled.div`
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: ${spacing[400]};

    @media (max-width: 720px) {
        grid-template-columns: 1fr;
    }
`;

const Consumer = styled.div`
    border: 1px solid ${({ color }) => color};
    border-top-width: ${({ $isNew }) => ($isNew ? "3px" : "1px")};
    padding: ${spacing[500]};
    min-width: 0;
    opacity: ${({ $isNew }) => ($isNew ? 1 : 0.75)};
`;

const ConsumerWho = styled.div`
    font-family: ${typography.fontFamily.display};
    font-size: ${typography.fontSize[200]};
    text-transform: uppercase;
    letter-spacing: ${typography.letterSpacing.wide};
    color: ${({ color }) => color};
    margin-bottom: ${spacing[200]};
`;

const ConsumerVia = styled.div`
    font-size: ${typography.fontSize[150]};
    line-height: ${typography.lineHeight[500]};
    color: ${({ theme }) => theme.subtleText};
`;

const NewTag = styled.span`
    display: inline-block;
    font-family: ${typography.fontFamily.display};
    font-size: ${typography.fontSize[100]};
    letter-spacing: ${typography.letterSpacing.widest};
    color: ${({ theme }) => theme.background};
    background: ${colors.amber[400]};
    padding: 0 ${spacing[200]};
    margin-left: ${spacing[300]};
`;

export const ThreeConsumers = () => (
    <Panel>
        <PanelLabel>Who Consumes the Design System</PanelLabel>
        <Consumers>
            <Consumer color={colors.cyan[300]}>
                <ConsumerWho color={colors.cyan[300]}>Designers</ConsumerWho>
                <ConsumerVia>Figma library</ConsumerVia>
            </Consumer>
            <Consumer color={colors.primary[500]}>
                <ConsumerWho color={colors.primary[500]}>Engineers</ConsumerWho>
                <ConsumerVia>Component library</ConsumerVia>
            </Consumer>
            <Consumer color={colors.amber[400]} $isNew>
                <ConsumerWho color={colors.amber[400]}>
                    AI Agents
                    <NewTag>New</NewTag>
                </ConsumerWho>
                <ConsumerVia>
                    Needs the same rules, expressed in a form it can actually read
                </ConsumerVia>
            </Consumer>
        </Consumers>
        <Caption>
            A design system built only for the first two produces inconsistency faster once the
            third one starts writing code.
        </Caption>
    </Panel>
);

// ===== The system, as connected layers =====
const LAYERS = [
    { name: "Design Tokens", desc: "The values everything else references", color: colors.cyan[300] },
    { name: "Figma Components", desc: "Modelled closely after the implementation", color: colors.cyan[300] },
    { name: "Frontend Components", desc: "React and TypeScript, the real contract", color: colors.primary[500] },
    { name: "AI Context + Rules", desc: "The same system, made machine-readable", color: colors.amber[400] },
    { name: "Generated Interfaces", desc: "Composed from approved parts, not invented", color: colors.emerald[500] },
];

export const SystemLayers = () => (
    <PipelineFlow
        label="Design System as Infrastructure"
        stages={LAYERS}
        caption="Every layer has to describe the same system. A Button cannot mean one thing in Figma, another in React, and something else again to the agent."
    />
);

// ===== Figma to code =====
const PIPELINE = [
    { name: "Figma Design", desc: "The source of truth designers actually work in", color: colors.cyan[300] },
    { name: "Design Context", desc: "Structure, not pixels", color: colors.cyan[300] },
    { name: "Component Identification", desc: "Which existing component this already is", color: colors.primary[500] },
    { name: "Implementation Rules", desc: "Approved variants, tokens, conventions", color: colors.amber[400] },
    { name: "React / TypeScript", desc: "Code that reuses instead of recreating", color: colors.emerald[500] },
];

export const FigmaPipeline = () => (
    <PipelineFlow
        label="Figma to Code"
        stages={PIPELINE}
        caption="Reasoning about components rather than pixels is what stops the biggest failure mode in design-to-code: rebuilding something that already exists."
    />
);

// ===== Hard rules =====
const Rules = styled.ol`
    list-style: none;
    margin: 0;
    padding: 0;
    counter-reset: rule;
`;

const Rule = styled.li`
    counter-increment: rule;
    display: flex;
    gap: ${spacing[400]};
    align-items: baseline;
    border-bottom: 1px solid ${({ theme }) => theme.borderLight};
    padding: ${spacing[400]} 0;
    font-family: ${typography.fontFamily.display};
    font-size: ${typography.fontSize[200]};
    text-transform: uppercase;
    letter-spacing: ${typography.letterSpacing.normal};
    color: ${({ theme }) => theme.text};
    line-height: ${typography.lineHeight[400]};

    &::before {
        content: "R" counter(rule, decimal-leading-zero);
        color: ${colors.coral[400]};
        flex: none;
    }

    &:last-child {
        border-bottom: none;
    }
`;

const HARD_RULES = [
    "Use existing components before creating new ones",
    "Use approved design tokens, never arbitrary values",
    "Do not invent unsupported component variants",
    "Follow established project structure and conventions",
    "Do not silently guess when required information is missing",
];

export const Guardrails = () => (
    <Panel>
        <PanelLabel>Hard Rules on Generation</PanelLabel>
        <Rules>
            {HARD_RULES.map((rule) => (
                <Rule key={rule}>{rule}</Rule>
            ))}
        </Rules>
        <Caption>
            The last one matters most. An agent that stops and asks is recoverable; one that guesses
            confidently is not.
        </Caption>
    </Panel>
);

// ===== Generate / validate loop =====




export const ValidationLoop = () => (
    <CycleFlow
        label="Generation Is Half the Pipeline"
        nodes={[
            { name: "Generate", color: colors.amber[400] },
            { name: "Validate", color: colors.cyan[300] },
            { name: "Correct", color: colors.coral[400] },
            { name: "Ship", color: colors.emerald[500] },
        ]}
        note="Fails validation — back to correct, not straight to ship"
        noteColor={colors.coral[400]}
        caption="Generated code is treated as an implementation that still has to satisfy the same constraints as human-written code: approved components, supported variants, tokens, conventions, accessibility."
    />
);
